/**
 * TORIARA NATURALS — Elite PDP System
 * pdp.js  v2.0  (post-audit remediation)
 *
 * FIXES APPLIED (forensic audit 26 May 2026):
 *  #1  Save badge: now reads from variant:change event detail (not stale DOM attrs)
 *  #2  ATC loading: now listens to 'ajaxProduct:added' (Impulse's actual event)
 *      Also listens to 'ajaxProduct:error' to clear on failure
 *  #5  Urgency row: hides/shows correctly on variant change
 *  #6  Sticky ATC: syncs price, disabled state, and button label on variant change
 *      aria-hidden toggled correctly when bar shows/hides
 *
 * Architecture:
 *  - Zero jQuery dependency
 *  - All state driven from Impulse's native CustomEvent stream
 *  - IIFE-scoped, no global leakage
 *  - Graceful degradation on missing DOM nodes
 *  - Reduced-motion respected
 */
(function () {
  'use strict';

  /* ─── Helpers ─────────────────────────────────────────────────────────── */
  function qs(sel, ctx)  { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }

  /* ═══════════════════════════════════════════════════════════════════════
     1. MOBILE STICKY ATC
     Appears when the real ATC button scrolls above the viewport.
     Syncs price, disabled state, and button label on every variant:change.
     aria-hidden toggled accurately — never announces hidden content.
  ═══════════════════════════════════════════════════════════════════════ */
  function initStickyATC() {
    var stickyBar = qs('.pdp-sticky-atc');
    var stickyBtn = qs('.pdp-sticky-atc__btn');
    var anchorBtn = qs('[data-pdp-atc-anchor]');

    if (!stickyBar || !anchorBtn) return;

    /* Price element in sticky bar */
    var stickyPriceEl = qs('.pdp-sticky-atc__price', stickyBar);

    /* ── Sync price text from rendered [data-product-price] ── */
    function syncStickyPrice() {
      var priceEl = qs('[data-product-price]');
      if (priceEl && stickyPriceEl) {
        stickyPriceEl.textContent = priceEl.textContent.trim();
      }
    }

    /* ── Sync disabled state + label from variant:change event ──
         FIX #6: reads variant object from event detail directly,
         not from stale server-rendered markup.                    */
    function syncStickyVariant(variant) {
      if (!stickyBtn || !variant) return;
      var available = variant.available;
      stickyBtn.disabled = !available;
      stickyBtn.textContent = available ? 'Add to Cart' : 'Sold Out';
      stickyBtn.setAttribute('aria-label',
        available
          ? 'Add to cart'
          : 'Currently sold out'
      );
    }

    /* Initial price sync */
    syncStickyPrice();

    /* Listen to Impulse's variant change stream */
    document.addEventListener('variant:change', function (e) {
      var variant = e.detail && e.detail.variant;
      syncStickyPrice();
      syncStickyVariant(variant);
    });

    /* ── IntersectionObserver — show/hide sticky bar ──
         FIX #6: aria-hidden toggled on every state change    */
    function setBarVisibility(visible) {
      stickyBar.classList.toggle('is-visible', visible);
      stickyBar.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            setBarVisibility(!entry.isIntersecting);
          });
        },
        { threshold: 0, rootMargin: '0px 0px -20px 0px' }
      );
      observer.observe(anchorBtn);
    } else {
      /* Scroll fallback for older browsers */
      var ticking = false;
      window.addEventListener('scroll', function () {
        if (!ticking) {
          requestAnimationFrame(function () {
            var rect = anchorBtn.getBoundingClientRect();
            setBarVisibility(rect.bottom < 0);
            ticking = false;
          });
          ticking = true;
        }
      }, { passive: true });
    }

    /* ── Sticky btn click → trigger real ATC button ── */
    if (stickyBtn) {
      stickyBtn.addEventListener('click', function () {
        var realBtn = qs('[data-add-to-cart][data-pdp-atc-anchor]');
        if (realBtn && !realBtn.disabled) {
          realBtn.click();
        }
      });
    }
  }

  /* ═══════════════════════════════════════════════════════════════════════
     2. ACCORDION SYSTEM
     Accessible, CSS max-height animated, keyboard + screen-reader safe.
     Reads aria state from HTML at init time (preserves server-set defaults).
  ═══════════════════════════════════════════════════════════════════════ */
  function initAccordions() {
    qsa('.pdp-accordion').forEach(function (panel) {
      var trigger = qs('.pdp-accordion__trigger', panel);
      var body    = qs('.pdp-accordion__body', panel);
      if (!trigger || !body) return;

      /* Generate unique ID for body → trigger aria-controls association */
      var bodyId = 'pdp-acc-body-' + Math.random().toString(36).slice(2, 8);
      body.id = bodyId;
      trigger.setAttribute('aria-controls', bodyId);

      /* Read initial state from server-set aria-expanded on the panel */
      var initiallyOpen = panel.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', String(initiallyOpen));
      body.setAttribute('aria-hidden', String(!initiallyOpen));

      trigger.addEventListener('click', function () {
        var isOpen = panel.getAttribute('aria-expanded') === 'true';
        panel.setAttribute('aria-expanded',  String(!isOpen));
        trigger.setAttribute('aria-expanded', String(!isOpen));
        body.setAttribute('aria-hidden',      String(isOpen));
      });
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     3. ATC BUTTON LOADING STATE
     FIX #2: listens to 'ajaxProduct:added' (Impulse's actual event).
     Also clears on 'ajaxProduct:error' so error states don't freeze the btn.
     Safety timeout reduced to 3s for faster recovery.
  ═══════════════════════════════════════════════════════════════════════ */
  function initATCLoadingState() {
    var form = qs('.product-single__form');
    if (!form) return;

    function setLoading(btn, loading) {
      if (!btn) return;
      btn.classList.toggle('loading', loading);
    }

    function clearAllLoading() {
      qsa('[data-add-to-cart]').forEach(function (b) {
        b.classList.remove('loading');
      });
    }

    /* Add loading class on form submit */
    form.addEventListener('submit', function () {
      var btn = qs('[data-add-to-cart]', form);
      setLoading(btn, true);
    });

    /* FIX #2: Correct Impulse event — 'ajaxProduct:added' not 'cart:add' */
    document.addEventListener('ajaxProduct:added', clearAllLoading);

    /* Also clear on error so the button doesn't stay stuck */
    document.addEventListener('ajaxProduct:error', clearAllLoading);

    /* Safety fallback — 3s instead of 4s */
    form.addEventListener('submit', function () {
      setTimeout(clearAllLoading, 3000);
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     4. SAVE BADGE — Variant-aware, event-driven
     FIX #1: reads variant object from 'variant:change' detail directly.
     No longer reads DOM data attributes (which Impulse never sets).
     The Liquid-rendered initial badge is correct on page load;
     this JS keeps it correct after every variant switch.
  ═══════════════════════════════════════════════════════════════════════ */
  function initSaveBadge() {
    var badge = qs('.pdp-save-badge');
    if (!badge) return;

    function updateBadge(variant) {
      if (!variant) return;
      if (variant.compare_at_price > variant.price && variant.price > 0) {
        var pct = Math.round(
          (variant.compare_at_price - variant.price) / variant.compare_at_price * 100
        );
        badge.textContent = 'Save ' + pct + '%';
        badge.setAttribute('aria-label', 'Save ' + pct + ' percent');
        badge.style.display = 'inline-flex';
      } else {
        badge.style.display = 'none';
      }
    }

    /* FIX #1: Impulse dispatches variant:change with detail.variant object */
    document.addEventListener('variant:change', function (e) {
      updateBadge(e.detail && e.detail.variant);
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     5. URGENCY ROW — Variant-aware availability signal
     FIX #5: hides row when selected variant is out-of-stock.
     On page load the server-rendered state is correct.
     After variant change, JS keeps it in sync.
  ═══════════════════════════════════════════════════════════════════════ */
  function initUrgencyRow() {
    var urgencyRow = qs('.pdp-urgency-row');
    if (!urgencyRow) return;

    document.addEventListener('variant:change', function (e) {
      var variant = e.detail && e.detail.variant;
      if (!variant) return;

      var available = variant.available;
      urgencyRow.style.display = available ? '' : 'none';
      /* Update aria-live region text when status changes */
      var textEl = qs('.pdp-urgency-text', urgencyRow);
      if (textEl && available) {
        textEl.innerHTML = '<em>In Stock</em> — Order today, dispatched within 1–2 business days';
      }
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     6. REVIEWS ANCHOR — Smooth scroll to Judge.me widget
  ═══════════════════════════════════════════════════════════════════════ */
  function initReviewsAnchor() {
    qsa('[data-scroll-to-reviews]').forEach(function (a) {
      a.addEventListener('click', function (e) {
        e.preventDefault();
        var target =
          qs('#jdgm-widget')    ||
          qs('.jdgm-widget')    ||
          qs('[id*="judge"]')   ||
          qs('[class*="jdgm"]') ||
          qs('[class*="review-widget"]');
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     7. VARIANT LABEL SYNC
     Shows selected value next to option name: "Size — 250g"
  ═══════════════════════════════════════════════════════════════════════ */
  function initVariantLabelSync() {
    function syncLabels() {
      qsa('.pdp-option-label').forEach(function (labelEl) {
        var optionName = labelEl.getAttribute('data-option-name');
        if (!optionName) return;
        var safeName = optionName.replace(/\s+/g, '-').toLowerCase();
        var checked =
          qs('input[type="radio"][name="' + safeName + '"]:checked') ||
          qs('input[type="radio"][name="' + optionName + '"]:checked');
        var valEl = qs('.pdp-option-selected-val', labelEl);
        if (checked && valEl) {
          valEl.textContent = '\u2014 ' + (checked.getAttribute('data-value') || checked.value);
        }
      });
    }

    syncLabels();
    document.addEventListener('change', function (e) {
      if (e.target && e.target.type === 'radio') syncLabels();
    });
  }

  /* ─── Initialise all modules on DOM ready ──────────────────────────── */
  function init() {
    initStickyATC();
    initAccordions();
    initATCLoadingState();
    initSaveBadge();
    initUrgencyRow();
    initReviewsAnchor();
    initVariantLabelSync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
